/**
 * `<PasswordInput>` — password field with visibility toggle.
 *
 * auth-flow-hardening Inc-4 (2026-05-20). Replaces the 5 raw
 * `<input type="password">` sites across the auth surfaces (login, signup
 * × 2, reset-password × 2) with a token-clean primitive that adds an
 * eye/eye-slash toggle.
 *
 * UX contract:
 * - Default state: type="password" (masked); toggle button shows the
 *   eye-open icon ("click to reveal").
 * - Toggled state: type="text" (revealed); toggle button shows the
 *   eye-slash icon ("click to hide").
 * - Toggle button is a real `<button type="button">` (NOT a div) — keyboard
 *   accessible, with `aria-pressed` + dynamic `aria-label` reflecting next
 *   action ("Show password" vs "Hide password"). Does NOT submit the
 *   parent form (type="button"). `tabIndex={-1}` keeps it out of Tab
 *   order — the form's primary interaction is type-then-submit; the
 *   toggle is a secondary mouse/touch affordance, so we don't insert it
 *   between fields in the Tab cycle.
 *
 * Variants:
 * - `variant="auth"` (default) — for the dedicated auth pages (login,
 *   reset-password): `px-4 py-3.5 text-md font-mono`. Toggle: 16x16 icon
 *   at right-3 with p-1.5.
 * - `variant="compact"` — for the SixQuestionForm signup wizard: `px-3
 *   py-2.5 text-sm font-body`. Toggle: 14x14 icon at right-2 with p-1.
 *
 * Why variants (not className override): Tailwind v4 has no specificity
 * ordering, so passing a `px-3` className to override the default `px-4`
 * is unreliable — both classes load at the same specificity and the
 * one defined later in the stylesheet wins, NOT the one passed later in
 * the className string. Variants encode the intent explicitly.
 *
 * Token discipline: text-text-muted (default), text-text-bright (hover) —
 * no raw hex. Mirrors the input border focus token (accent/30).
 */
import { useState, type InputHTMLAttributes, forwardRef } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import clsx from 'clsx';

interface PasswordInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /**
   * Optional additional className (merged after the variant baseline).
   * Do NOT pass `type` — it's hard-coded. Do NOT pass `px-*` /
   * `py-*` / `text-*` overrides via this prop; use the `variant` prop
   * instead (Tailwind v4 has no specificity ordering).
   */
  className?: string;
  /**
   * Visual variant. `'auth'` (default) matches login + reset-password
   * shape; `'compact'` matches SixQuestionForm signup wizard shape.
   */
  variant?: 'auth' | 'compact';
}

const AUTH_INPUT_CLASS =
  'w-full px-4 py-3.5 pr-12 bg-input-bg border border-input-border rounded-[3px] text-text-bright font-mono text-md outline-none focus:border-accent/30 transition-colors disabled:opacity-50';

const COMPACT_INPUT_CLASS =
  'w-full px-3 py-2.5 pr-10 bg-input-bg border border-input-border rounded-[3px] text-text-bright font-body text-sm outline-none focus:border-accent/30 transition-colors disabled:opacity-50';

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, variant = 'auth', ...props }, ref) => {
    const [visible, setVisible] = useState(false);
    const baseClass =
      variant === 'compact' ? COMPACT_INPUT_CLASS : AUTH_INPUT_CLASS;
    const togglePosition =
      variant === 'compact' ? 'right-2 p-1' : 'right-3 p-1.5';
    const iconSize = variant === 'compact' ? 'w-3.5 h-3.5' : 'w-4 h-4';

    return (
      <div className="relative">
        <input
          {...props}
          ref={ref}
          type={visible ? 'text' : 'password'}
          className={clsx(baseClass, className)}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
          data-testid="password-visibility-toggle"
          tabIndex={-1}
          className={clsx(
            'absolute top-1/2 -translate-y-1/2 text-text-muted hover:text-text-bright transition-colors rounded-[2px] focus:outline-none focus:text-text-bright focus:bg-input-border/30',
            togglePosition
          )}
        >
          {visible ? (
            <EyeOff className={iconSize} aria-hidden="true" />
          ) : (
            <Eye className={iconSize} aria-hidden="true" />
          )}
        </button>
      </div>
    );
  }
);

PasswordInput.displayName = 'PasswordInput';
